// /src/pages/Root.jsx
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Grid, List, Book, Trees, Flower, TreeDeciduous, Palmtree, LeafyGreen, GitBranch, Users, Settings, Shield, Trash2, Edit, Plus } from 'lucide-react'
import { useAuth } from '../utils/authContext'
import usePlantStore from '../utils/plantStore'
import PlantCard from '../components/PlantCard'
import ImageUpload from '../components/ImageUpload'
import Layout from '../components/Layout'

const Root = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState('grid')
  const [activeTab, setActiveTab] = useState('plants')
  const { plants, fetchPlants, addPlant, updatePlant, deletePlant } = usePlantStore()
  
  // User management state
  const [users, setUsers] = useState([])
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [createUserForm, setCreateUserForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'viewer'
  })

  // Fetch plants and users on component mount
  useEffect(() => {
    fetchPlants()
    fetchUsers()
  }, [fetchPlants])



  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken')
      const response = await fetch('/api/auth/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (response.ok) {
        const data = await response.json()
        setUsers(data.users || [])
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
    }
  }

  const handleCreateUser = async (e) => {
    e.preventDefault()
    try {
      const token = localStorage.getItem('adminToken')
      const formData = new FormData()
      formData.append('adminToken', token)
      formData.append('email', createUserForm.email)
      formData.append('password', createUserForm.password)
      formData.append('name', createUserForm.name)
      formData.append('role', createUserForm.role)

      const response = await fetch('/api/auth/create-user', {
        method: 'POST',
        body: formData
      })

      if (response.ok) {
        setShowCreateUser(false)
        setCreateUserForm({ email: '', password: '', name: '', role: 'viewer' })
        fetchUsers() // Refresh user list
      }
    } catch (error) {
      console.error('Failed to create user:', error)
    }
  }

  const categories = [
    { id: 'all', name: 'All Plants', icon: Book, count: plants.length },
    { id: 'trees', name: 'Trees', icon: Trees, count: plants.filter(p => p.category === 'trees').length },
    { id: 'flowers', name: 'Flowers', icon: Flower, count: plants.filter(p => p.category === 'flowers').length },
    { id: 'succulents', name: 'Succulents', icon: TreeDeciduous, count: plants.filter(p => p.category === 'succulents').length },
    { id: 'palms', name: 'Palms', icon: Palmtree, count: plants.filter(p => p.category === 'palms').length },
    { id: 'ferns', name: 'Ferns', icon: LeafyGreen, count: plants.filter(p => p.category === 'ferns').length },
    { id: 'climbers', name: 'Climbers', icon: GitBranch, count: plants.filter(p => p.category === 'climbers').length },
  ]

  const filteredPlants = plants.filter(plant => {
    const matchesSearch = plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plant.scientificName.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || plant.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  return (
    <Layout title="Super Admin Dashboard">
      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex space-x-8 border-b border-forest-200">
            <button
              onClick={() => setActiveTab('plants')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'plants'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-forest-500 hover:text-forest-700'
              }`}
            >
              <Book className="w-4 h-4 inline mr-2" />
              Plant Management
            </button>
            <button
              onClick={() => setActiveTab('add')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'add'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-forest-500 hover:text-forest-700'
              }`}
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Add Plant
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-forest-500 hover:text-forest-700'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              User Management
            </button>
          </div>
        </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'add' ? (
          <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6">
            <h2 className="text-xl font-semibold text-forest-900 mb-6">Add New Plant</h2>
            <p className="text-forest-600 mb-6">Super admin can add new plants to the database.</p>
            {/* Add plant form would go here - for now just a placeholder */}
            <div className="text-center py-8">
              <Plus className="w-12 h-12 text-forest-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-forest-900 mb-2">Add Plant Form</h3>
              <p className="text-forest-600">Plant addition functionality will be implemented here.</p>
            </div>
          </div>
        ) : activeTab === 'plants' ? (
          <>
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-forest-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search plants by name or scientific name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-forest-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'bg-white text-forest-600'}`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'bg-white text-forest-600'}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-white text-forest-600 hover:bg-forest-50'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  <span>{category.name}</span>
                  <span className="text-xs bg-forest-100 text-forest-600 px-2 py-1 rounded-full">
                    {category.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Results */}
            <div className="mb-4">
              <p className="text-forest-600">
                Showing {filteredPlants.length} of {plants.length} plants
              </p>
            </div>

            {/* Plants Grid/List */}
            {plants.length === 0 ? (
              <div className="text-center py-12">
                <Book className="w-16 h-16 text-forest-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-forest-900 mb-2">Plant Database is Empty</h3>
                <p className="text-forest-600 mb-4">No plants have been added to the database yet.</p>
                <div className="bg-forest-50 border border-forest-200 rounded-lg p-4 max-w-md mx-auto mb-6">
                  <p className="text-sm text-forest-700 mb-2">
                    <strong>Getting Started:</strong>
                  </p>
                  <ul className="text-sm text-forest-600 space-y-1 text-left">
                    <li>• Use the "Add Plant" tab to create your first plant</li>
                    <li>• Upload images to make your plants more engaging</li>
                    <li>• Add detailed descriptions and care instructions</li>
                  </ul>
                </div>
                <button
                  onClick={() => setActiveTab('add')}
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add First Plant</span>
                </button>
              </div>
            ) : filteredPlants.length === 0 ? (
              <div className="text-center py-12">
                <Book className="w-12 h-12 text-forest-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-forest-900 mb-2">No plants found</h3>
                <p className="text-forest-600">Try adjusting your search or filter criteria.</p>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'space-y-4'}>
                {filteredPlants.map((plant) => (
                  <PlantCard key={plant.id} plant={plant} viewMode={viewMode} showActions={true} />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* User Management */}
            <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-forest-900">User Management</h2>
                <button
                  onClick={() => setShowCreateUser(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create User</span>
                </button>
              </div>

              {/* User List */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-forest-200">
                  <thead className="bg-forest-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-forest-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-forest-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-forest-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-forest-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-forest-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-forest-200">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-forest-900">{user.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-forest-500">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                            user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-forest-500">
                          <button className="text-primary-600 hover:text-primary-900 mr-3">
                            <Edit className="w-4 h-4" />
                          </button>
                          {/* Prevent super admin from deleting other super admins */}
                          {user.role !== 'super_admin' && (
                            <button className="text-red-600 hover:text-red-900">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Create User Modal */}
            {showCreateUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold text-forest-900 mb-4">Create New User</h3>
                  <form onSubmit={handleCreateUser}>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={createUserForm.name}
                          onChange={(e) => setCreateUserForm({...createUserForm, name: e.target.value})}
                          className="w-full px-3 py-2 border border-forest-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={createUserForm.email}
                          onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})}
                          className="w-full px-3 py-2 border border-forest-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">Password</label>
                        <input
                          type="password"
                          value={createUserForm.password}
                          onChange={(e) => setCreateUserForm({...createUserForm, password: e.target.value})}
                          className="w-full px-3 py-2 border border-forest-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">Role</label>
                        <select
                          value={createUserForm.role}
                          onChange={(e) => setCreateUserForm({...createUserForm, role: e.target.value})}
                          className="w-full px-3 py-2 border border-forest-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        type="button"
                        onClick={() => setShowCreateUser(false)}
                        className="px-4 py-2 text-forest-600 hover:text-forest-800"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                      >
                        Create User
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  )
}

export default Root 